import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import PageHeader from '../../ui-components/PageHeader';

const T = {
  primary: '#526075', tertiary: '#635983', tertiaryContainer: '#dbcdff',
  primaryContainer: '#d5e3fd', surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const tools = [
  {
    path: '/SRTGenerator',
    label: 'SRT Generator',
    desc: 'Upload een video of audio en genereer automatisch ondertitels met AI. Vertaal, bewerk en exporteer als SRT.',
    icon: 'subtitles',
    color: '#635983',
    bg: '#dbcdff',
    tags: [{ label: 'AI', color: '#526075', bg: '#d5e3fd' }],
  },
  {
    path: '/SRTDashboard',
    label: 'SRT Beheer',
    desc: 'Overzicht van alle ondertiteling jobs. Bekijk status, download resultaten en beheer vertalingen.',
    icon: 'dashboard',
    color: '#3b82f6',
    bg: '#dbeafe',
    tags: [{ label: 'Beta', color: '#7c3aed', bg: '#ede9fe' }],
  },
  {
    path: '/ritmo',
    label: 'Ondertitel Studio',
    desc: 'Bewerk ritmische ondertitels in de browser en zet render-jobs klaar voor de lokale render-server.',
    icon: 'movie_filter',
    color: '#4f46e5',
    bg: '#e0e7ff',
    tags: [{ label: 'Nieuw', color: '#4f46e5', bg: '#e0e7ff' }],
  },
];

export default function Ondertiteling() {
  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>
      <PageHeader
        icon="subtitles"
        title="Ondertiteling"
        subtitle="Genereer, beheer en exporteer ondertitels"
        accent={{ color: T.tertiary, bg: T.tertiaryContainer }}
      />

      {/* Tool cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {tools.map(tool => (
          <Box
            key={tool.path}
            component={Link}
            to={tool.path}
            sx={{
              display: 'flex', alignItems: 'center', gap: 2.5,
              textDecoration: 'none', color: 'inherit',
              bgcolor: T.surface, borderRadius: '0.75rem', p: 3,
              border: '1px solid rgba(45,51,53,0.06)',
              transition: 'all 0.15s',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(45,51,53,0.07)',
                borderColor: `${tool.color}25`,
                transform: 'translateY(-2px)',
                '& .tool-arrow': { opacity: 1, transform: 'translateX(0)' },
              },
            }}
          >
            <Box sx={{
              width: 48, height: 48, borderRadius: '0.625rem',
              bgcolor: tool.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Icon name={tool.icon} size={26} sx={{ color: tool.color }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 700, color: T.text }}>
                  {tool.label}
                </Typography>
                {tool.tags.map(tag => (
                  <Box key={tag.label} sx={{
                    fontSize: '0.5rem', fontWeight: 800, fontFamily: T.fontBody,
                    color: tag.color, bgcolor: tag.bg,
                    px: 0.75, py: 0.2, borderRadius: '4px',
                    textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.4,
                  }}>
                    {tag.label}
                  </Box>
                ))}
              </Box>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline, lineHeight: 1.5 }}>
                {tool.desc}
              </Typography>
            </Box>
            <Box className="tool-arrow" sx={{ opacity: 0, transform: 'translateX(-4px)', transition: 'all 0.2s', flexShrink: 0 }}>
              <Icon name="arrow_forward" size={20} sx={{ color: tool.color }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
